import { useCallback, useEffect, useRef, useState } from 'react';

import { OtpInput } from '@/auth/components/shared';
import { PriceSummary } from '@/components/SchedulingForm';
import { EmailDeliveryHelp } from '@/componentsv2/EmailDeliveryHelp';
import { useRateDisplay } from '@/hooks/useRateDisplay';
import { RateDisplayType, toCreditsPerHour } from '@/utils/rateDisplay';

import { OTP_CONFIG } from '../../constants';
import type { OTPVerificationStepProps } from '../../types';
import { useStyles } from './OTPVerificationStep.styles';

export function OTPVerificationStep({
  isReturningUser,
  userName,
  userCompany,
  maskedAddress,
  transport,
  expert,
  duration,
  hourlyRate,
  onVerify,
  onResend,
  onUseAnotherAccount,
  onBack,
  isLoading,
  error,
}: OTPVerificationStepProps): JSX.Element {
  const s = useStyles();
  const displayType = useRateDisplay();
  const showUSD = displayType === RateDisplayType.USD;

  const [code, setCode] = useState<string>('');

  // Guard to prevent duplicate auto-submit calls
  const hasVerifiedRef = useRef(false);

  // Resend cooldown state - explicitly typed as number to avoid literal type inference
  const [resendCooldown, setResendCooldown] = useState<number>(OTP_CONFIG.RESEND_COOLDOWN_SECONDS);
  const [canResend, setCanResend] = useState(false);

  // Start cooldown timer on mount
  useEffect(() => {
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Reset cooldown when resend is triggered
  const handleResend = useCallback(() => {
    if (!canResend || isLoading) return;

    setCanResend(false);
    setResendCooldown(OTP_CONFIG.RESEND_COOLDOWN_SECONDS);
    setCode('');
    onResend();
  }, [canResend, isLoading, onResend]);

  const isCodeComplete = code.length === OTP_CONFIG.CODE_LENGTH;

  // Reset verification guard when code changes (user is typing new code)
  useEffect(() => {
    hasVerifiedRef.current = false;
  }, [code]);

  // Auto-submit when code is complete (only once per code entry)
  useEffect(() => {
    if (isCodeComplete && !isLoading && !hasVerifiedRef.current) {
      hasVerifiedRef.current = true;
      onVerify(code);
    }
  }, [isCodeComplete, code, isLoading, onVerify]);

  const handleVerify = useCallback(() => {
    if (!isCodeComplete || isLoading) return;
    onVerify(code);
  }, [isCodeComplete, isLoading, code, onVerify]);

  // Clear code when an error surfaces so the user can retype
  useEffect(() => {
    if (error) {
      setCode('');
    }
  }, [error]);

  return (
    <>
      <div className={s.formContent}>
        {/* Welcome banner for returning users */}
        {isReturningUser && userName && (
          <div className={s.welcomeBanner}>
            <p className={s.welcomeTitle}>
              Welcome back, <span className={s.welcomeName}>{userName}</span>!
            </p>
            {userCompany && <p className={s.welcomeCompany}>{userCompany}</p>}
          </div>
        )}

        {/* Instructions */}
        {!isReturningUser && (
          <p className={s.instructions}>
            We sent a {OTP_CONFIG.CODE_LENGTH}-digit code to{' '}
            <span className="address">{maskedAddress}</span>
          </p>
        )}

        {/* Error message */}
        {error && (
          <div className={s.errorMessage}>
            <p className={s.errorText}>{error}</p>
          </div>
        )}

        {/* Code input label */}
        <div className={s.codeLabel}>Enter verification code</div>

        <div className={s.codeInputContainer}>
          <OtpInput
            value={code}
            onChange={setCode}
            length={OTP_CONFIG.CODE_LENGTH}
            disabled={isLoading}
            hasError={Boolean(error)}
            autoFocusFirstCell={Boolean(error)}
          />
        </div>

        {/* Resend link with cooldown */}
        <div className={s.resendSection}>
          {canResend ? (
            <button
              type="button"
              className={s.resendButton}
              onClick={handleResend}
              disabled={isLoading}
            >
              <span>Resend code via </span>
              <span>{transport === 'SMS' ? 'SMS' : 'email'}</span>
            </button>
          ) : (
            <span className={s.cooldownText}>
              <span>Resend code via </span>
              <span>{transport === 'SMS' ? 'SMS' : 'email'}</span>
              <span> ({resendCooldown}s)</span>
            </span>
          )}
          {transport === 'EMAIL' && (
            <div>
              <EmailDeliveryHelp align="center" className="mt-8" />
            </div>
          )}
        </div>

        {/* "Not you?" link for returning users */}
        {isReturningUser && userName && (
          <div className={s.notYouSection}>
            <span className={s.notYouText}>Not {userName.split(' ')[0]}?</span>
            <button
              type="button"
              className={s.notYouLink}
              onClick={onUseAnotherAccount}
              disabled={isLoading}
            >
              Use a different account
            </button>
          </div>
        )}

        {/* Price summary */}
        <PriceSummary
          duration={duration}
          pricing={
            showUSD
              ? { type: 'dollars', hourlyRate }
              : { type: 'credits', creditRate: toCreditsPerHour(expert.creditRate) }
          }
          showChargeNote
        />
      </div>

      {/* Footer with buttons */}
      <div className={s.footer}>
        <button type="button" className={s.backButton} onClick={onBack} disabled={isLoading}>
          Back
        </button>
        <button
          type="button"
          className={s.verifyButton}
          onClick={handleVerify}
          disabled={!isCodeComplete || isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>
      </div>
    </>
  );
}

export default OTPVerificationStep;
